import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import type {
  AiAudienceSuggestResponse,
  AudienceFilters,
  AudienceMatchResponse,
  ProfileAttributeFilter,
  PublishConfigDraft,
  PublishConfigTargetAudience,
  ResponseTargetingMode,
  ScientificConfidenceLevel,
} from '@brushfire/types'
import { DEFAULT_SELECTED_CHANNELS } from '@/features/instruments/publish/distribution-channels/lib/distributionChannelCatalog'
import type { DistributionChannelId } from '@/features/instruments/publish/distribution-channels/lib/distributionChannelTypes'
import {
  CHANNEL_CONTENT_KEYS,
  type ChannelContentDraft,
  type ChannelContentPatch,
} from '@/features/instruments/publish/distribution-channels/lib/channelContentTypes'
import {
  DEFAULT_RESPONSE_TARGET,
  EMPTY_TAB_CONFIRMATIONS,
  type BudgetTabConfirmations,
  type BudgetWizardTab,
} from '@/features/instruments/publish/campaign-budget/lib/campaignBudgetConstants'
import {
  DEFAULT_REWARD_ENABLED,
  rewardEnabledFromPublishConfig,
} from '@/features/instruments/publish/shared/lib/rewardEnabled'
import { instrumentApiPath } from '@/features/instruments/infra/instrumentPaths'
import { coerceDemographicPanelIds } from '@/features/instruments/publish/target-audience/lib/audienceFilterLogic'
import { coerceAudienceFilters } from '@/features/instruments/publish/target-audience/lib/deviceTypeFilter'
import {
  buildPublishTargetAudienceSnapshot,
  emptyPublishTargetAudience,
  normalizePublishTargetAudience,
} from '@/features/instruments/publish/shared/lib/publishTargetAudience'

export type PublishDraftPayload = {
  targetAudience: PublishConfigTargetAudience
  selectedChannels: DistributionChannelId[]
  /** BFP-598 legacy global copy — read-only; seeds `channelContent` for older drafts. */
  notificationTitle?: string
  notificationBody?: string
  /** BFP-1144 — stored per-channel customizations. */
  channelContent?: ChannelContentDraft
  rewardEnabled: boolean
  budgetConfigured: boolean
  responseTarget: number | null
  rewardPerResponse: number | null
  activeBudgetTab: BudgetWizardTab
  budgetTabConfirmations: BudgetTabConfirmations
  wizardStep: number
  maxReachedStep: number
  targetingMode?: ResponseTargetingMode | null
  confidenceLevel?: ScientificConfidenceLevel
  marginOfErrorPercent?: number
  scientificTargetLocked?: boolean
  step4Phase?: 'mode_selection' | 'standard_tabs' | 'scientific'
  audienceAvailability?: import('@brushfire/types').AudienceAvailability
}

function coerceBudgetWizardTab(value: string | undefined): BudgetWizardTab {
  if (value === 'reward_per_response' || value === 'campaign_budget') {
    return value
  }
  return 'response_target'
}

function coerceBudgetTabConfirmations(
  value: Record<string, boolean> | undefined,
): BudgetTabConfirmations {
  if (!value) {
    return { ...EMPTY_TAB_CONFIRMATIONS }
  }
  return {
    responseTarget: value.responseTarget ?? false,
    rewardPerResponse: value.rewardPerResponse ?? false,
    campaignBudgetViewed: value.campaignBudgetViewed ?? false,
  }
}

/** `channelContent` is read through a local type — CI clones shared types without BFP-1144 fields. */
type PublishConfigDraftWithChannelContent = PublishConfigDraft & {
  channelContent?: ChannelContentPatch | null
}

function channelContentFromServer(
  raw: ChannelContentPatch | null | undefined,
): ChannelContentDraft | undefined {
  if (raw == null) return undefined
  const content: ChannelContentDraft = {}
  for (const key of CHANNEL_CONTENT_KEYS) {
    const value = raw[key]
    if (value != null) Object.assign(content, { [key]: value })
  }
  return Object.keys(content).length > 0 ? content : undefined
}

type PublishConfigPatchBody = Omit<Partial<PublishConfigDraft>, 'channelContent'> & {
  channelContent?: ChannelContentPatch
}

/** PATCH input — `channelContent: { sms: null }` restores that channel's system default. */
export type PublishDraftPatchInput = Omit<Partial<PublishDraftPayload>, 'channelContent'> & {
  channelContent?: ChannelContentPatch
}

function draftFromServer(raw: PublishConfigDraft): PublishDraftPayload {
  const data = raw as PublishConfigDraftWithChannelContent
  const channelContent = channelContentFromServer(data.channelContent)
  return {
    targetAudience: normalizePublishTargetAudience(
      data.targetAudience ?? emptyPublishTargetAudience(),
    ),
    selectedChannels: data.selectedChannels ?? [...DEFAULT_SELECTED_CHANNELS],
    ...(data.notificationTitle?.trim()
      ? { notificationTitle: data.notificationTitle.trim() }
      : {}),
    ...(data.notificationBody?.trim()
      ? { notificationBody: data.notificationBody.trim() }
      : {}),
    ...(channelContent ? { channelContent } : {}),
    rewardEnabled: rewardEnabledFromPublishConfig(data),
    budgetConfigured: data.budgetConfigured ?? false,
    responseTarget:
      data.audienceAvailability === 'existing_and_future'
        ? (data.responseTarget ?? null)
        : (data.responseTarget ?? DEFAULT_RESPONSE_TARGET),
    rewardPerResponse: data.rewardPerResponse ?? null,
    activeBudgetTab: coerceBudgetWizardTab(data.activeBudgetTab),
    budgetTabConfirmations: coerceBudgetTabConfirmations(data.budgetTabConfirmations),
    wizardStep: data.wizardStep ?? 1,
    maxReachedStep: data.maxReachedStep ?? 1,
    targetingMode: data.targetingMode ?? null,
    confidenceLevel: data.confidenceLevel,
    marginOfErrorPercent: data.marginOfErrorPercent,
    scientificTargetLocked: data.scientificTargetLocked ?? false,
    step4Phase: data.step4Phase,
    audienceAvailability: data.audienceAvailability,
  }
}

function applyWizardStepPatch(
  patch: PublishConfigPatchBody,
  payload: PublishDraftPatchInput,
): void {
  if (payload.wizardStep != null) patch.wizardStep = payload.wizardStep
  if (payload.maxReachedStep != null) patch.maxReachedStep = payload.maxReachedStep
}

function applyTargetAudiencePatch(
  patch: PublishConfigPatchBody,
  targetAudience: PublishDraftPayload['targetAudience'],
): void {
  patch.targetAudience = buildPublishTargetAudienceSnapshot({
    filters: targetAudience.filters,
    aiSuggestedFilters: targetAudience.aiSuggestedFilters ?? {},
    suggestedPanelIds: targetAudience.suggestedPanelIds,
    userModifiedPanelIds: targetAudience.userModifiedPanelIds,
    filtersModified: targetAudience.filtersModified === true,
    aiSuggestionsApplied: targetAudience.aiSuggestionsApplied === true,
    count: targetAudience.count,
    profileAttributeFilters: targetAudience.profileAttributeFilters,
  })
}

function applyBudgetPatchFields(
  patch: PublishConfigPatchBody,
  payload: PublishDraftPatchInput,
): void {
  if (payload.selectedChannels != null) patch.selectedChannels = payload.selectedChannels
  if (payload.channelContent != null) patch.channelContent = { ...payload.channelContent }
  if (payload.rewardEnabled != null) patch.rewardEnabled = payload.rewardEnabled
  if (payload.budgetConfigured != null) patch.budgetConfigured = payload.budgetConfigured
  if (payload.responseTarget !== undefined) patch.responseTarget = payload.responseTarget
  if (payload.rewardPerResponse !== undefined) patch.rewardPerResponse = payload.rewardPerResponse
  if (payload.activeBudgetTab != null) patch.activeBudgetTab = payload.activeBudgetTab
  if (payload.budgetTabConfirmations != null) {
    patch.budgetTabConfirmations = { ...payload.budgetTabConfirmations }
  }
}

function applyScientificPatchFields(
  patch: PublishConfigPatchBody,
  payload: PublishDraftPatchInput,
): void {
  if (payload.targetingMode !== undefined) patch.targetingMode = payload.targetingMode ?? undefined
  if (payload.confidenceLevel != null) patch.confidenceLevel = payload.confidenceLevel
  if (payload.marginOfErrorPercent != null) patch.marginOfErrorPercent = payload.marginOfErrorPercent
  if (payload.scientificTargetLocked != null) {
    patch.scientificTargetLocked = payload.scientificTargetLocked
  }
  if (payload.step4Phase != null) patch.step4Phase = payload.step4Phase
  if (payload.audienceAvailability != null) {
    patch.audienceAvailability = payload.audienceAvailability
  }
}

function draftToPatchPayload(payload: PublishDraftPatchInput): PublishConfigPatchBody {
  const patch: PublishConfigPatchBody = {}
  applyWizardStepPatch(patch, payload)
  if (payload.targetAudience != null) {
    applyTargetAudiencePatch(patch, payload.targetAudience)
  }
  applyBudgetPatchFields(patch, payload)
  applyScientificPatchFields(patch, payload)
  return patch
}

export async function getPublishDraft(instrumentId: string): Promise<PublishDraftPayload> {
  const { data } = await apiClient.get<ApiResponse<PublishConfigDraft>>(
    instrumentApiPath(instrumentId, '/publish-draft'),
  )
  return draftFromServer(data.data ?? { wizardStep: 1, maxReachedStep: 1, rewardEnabled: DEFAULT_REWARD_ENABLED })
}

export async function patchPublishDraft(
  instrumentId: string,
  payload: PublishDraftPatchInput,
): Promise<PublishDraftPayload> {
  const { data } = await apiClient.patch<ApiResponse<PublishConfigDraft>>(
    instrumentApiPath(instrumentId, '/publish-draft'),
    draftToPatchPayload(payload),
  )
  return draftFromServer(data.data ?? { wizardStep: 1, maxReachedStep: 1, rewardEnabled: DEFAULT_REWARD_ENABLED })
}

type AudienceSuggestApiPayload = AiAudienceSuggestResponse & {
  aiBaseline?: AudienceFilters
}

export async function fetchAiAudienceSuggestions(
  instrumentId: string,
  options?: { refresh?: boolean },
): Promise<AiAudienceSuggestResponse> {
  const { data } = await apiClient.get<ApiResponse<AudienceSuggestApiPayload>>(
    instrumentApiPath(instrumentId, '/audience/suggest'),
    options?.refresh ? { params: { refresh: 'true' } } : undefined,
  )
  if (!data.data) {
    throw new Error('Audience suggestions missing from response')
  }
  const raw = data.data
  const aiSuggestedFilters = coerceAudienceFilters(
    raw.aiSuggestedFilters ?? raw.aiBaseline ?? raw.filters ?? {},
  )
  const filters = coerceAudienceFilters(raw.filters ?? {})
  return {
    filters,
    aiSuggestedFilters,
    suggestedPanelIds: coerceDemographicPanelIds(raw.suggestedPanelIds),
    userModifiedPanelIds: coerceDemographicPanelIds(raw.userModifiedPanelIds),
  }
}

export async function fetchAudienceMatchCount(
  instrumentId: string,
  filters: AudienceFilters,
  profileAttributeFilters: ProfileAttributeFilter[] = [],
): Promise<AudienceMatchResponse> {
  const matchedProfileFilters = profileAttributeFilters.filter((filter) =>
    filter.values.some((entry) => entry.trim().length > 0),
  )
  const { data } = await apiClient.post<ApiResponse<AudienceMatchResponse>>(
    instrumentApiPath(instrumentId, '/audience/match'),
    { filters, profileAttributeFilters: matchedProfileFilters },
  )
  if (!data.data) {
    throw new Error('Audience match count missing from response')
  }
  return data.data
}
